import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getClient, updateClient, deleteClient, getVenues, getProjects, getQuotes, getInvoices, updateQuote, adminMarkQuoteAccepted, sendQuoteByEmailDefault, viewQuotePdf, downloadQuotePdf, viewInvoicePdf, downloadInvoicePdf, extractErrorMessage, getVenueLocations, removeClientVenueAssociation } from "../lib/api";
import {
  PageHeader,
  LoadingSpinner,
  StatusBadge,
} from "../components/shared/DataComponents";
import { useFormatters } from "../components/shared/useFormatters";
import { DestructiveConfirmDialog } from "../components/shared/DestructiveConfirmDialog";
import { ClientContactsSection } from "../components/ClientContactsSection";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
// EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
import {
  CLIENT_TAX_TREATMENTS,
  CLIENT_ENTITY_TYPES,
  VAT_NUMBER_STATUSES,
  DEFAULT_CLIENT_TAX_TREATMENT,
  DEFAULT_CLIENT_ENTITY_TYPE,
  DEFAULT_VAT_NUMBER_STATUS,
} from "../lib/taxEnums";
// EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1
import { HelpPopover } from "../components/shared/HelpPopover";
import { CountrySelect } from "../components/shared/CountrySelect";
import { ClientTaxCodeValidation } from "../components/financial/ClientTaxCodeValidation";
import {
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Globe,
  CreditCard,
  FileText,
  Pencil,
  Trash2,
  Loader2,
  Save,
  X,
  FolderKanban,
  ExternalLink,
  Percent,
  Users,
  Receipt,
  Download,
  Eye,
  Send,
  CheckCircle2,
  EyeOff,
  ThumbsUp,
} from "lucide-react";

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { formatDate, formatCurrency, currencySymbol } = useFormatters();
  
  const [client, setClient] = useState(null);
  const [venues, setVenues] = useState([]);
  const [projects, setProjects] = useState([]);
  // EVENTSFLO-ADMIN-CLIENT-QUOTES-INVOICES-VISIBILITY-DIAGNOSTIC-FIX-V1
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // EVENTSFLO-CLIENT-PROFILE-VENUE-LOCATIONS-AND-REMOVE-ASSOCIATION-V1
  const [expandedVenueId, setExpandedVenueId] = useState(null);
  const [venueLocationsByVenue, setVenueLocationsByVenue] = useState({});
  const [venueLocationsLoading, setVenueLocationsLoading] = useState(false);
  const [removeAssocVenue, setRemoveAssocVenue] = useState(null);
  const [removingAssoc, setRemovingAssoc] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    contact_email: "",
    contact_phone: "",
    billing_address: "",
    // EVENTSFLO-LIVE-DATA-ENTRY-VENUE-LOCATION-FILENAME-ADDRESS-UX-V1
    // EVENTSFLO-CLIENT-VENUE-ADDRESS-UI-REGRESSION-FIX-V1
    // Structured address for multi-line PDF display + Country (uses existing
    // `billing_country` ISO-2 field defined further below in this state).
    address_line_1: "",
    address_line_2: "",
    postcode: "",
    town_city: "",
    status: "active",
    reference_code: "",
    website: "",
    currency: "",
    vat_rate: "",
    vat_code: "",
    fax: "",
    notes: "",
    logo_url: "",
    // EVENTSFLO-CLIENT-PROFILE-CUSTOMISATION-LOGO-V1
    portal_display_name: "",
    portal_notes: "",
    // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1 — Tax Profile
    billing_country: "",
    client_entity_type: DEFAULT_CLIENT_ENTITY_TYPE,
    vat_number_status: DEFAULT_VAT_NUMBER_STATUS,
    client_tax_treatment: DEFAULT_CLIENT_TAX_TREATMENT,
  });
  const [logoUploading, setLogoUploading] = useState(false);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [clientRes, venuesRes, projectsRes, quotesRes, invoicesRes] = await Promise.all([
        getClient(clientId),
        getVenues(),
        getProjects(),
        // EVENTSFLO-ADMIN-CLIENT-QUOTES-INVOICES-VISIBILITY-DIAGNOSTIC-FIX-V1
        getQuotes({ client_id: clientId, limit: 200 }).catch(() => ({ data: [] })),
        getInvoices({ client_id: clientId, limit: 200 }).catch(() => ({ data: [] })),
      ]);
      
      const clientData = clientRes.data;
      setClient(clientData);
      
      // EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
      // Filter venues for this client using the canonical 3-case rule:
      // owned OR shared OR client_copy. Locations of those venues are
      // handled by the Venues page itself.
      setVenues((venuesRes.data || []).filter(v => (
        v.client_id === clientId ||
        (Array.isArray(v.shared_client_ids) && v.shared_client_ids.includes(clientId)) ||
        v.copied_for_client_id === clientId
      )));
      setProjects((projectsRes.data || []).filter(p => p.client_id === clientId));
      setQuotes(quotesRes.data || []);
      setInvoices(invoicesRes.data || []);
      
      // Populate form
      setFormData({
        name: clientData.name || "",
        contact_email: clientData.contact_email || "",
        contact_phone: clientData.contact_phone || "",
        billing_address: clientData.billing_address || "",
        // EVENTSFLO-LIVE-DATA-ENTRY-VENUE-LOCATION-FILENAME-ADDRESS-UX-V1
        // EVENTSFLO-CLIENT-VENUE-ADDRESS-UI-REGRESSION-FIX-V1
        address_line_1: clientData.address_line_1 || "",
        address_line_2: clientData.address_line_2 || "",
        postcode: clientData.postcode || "",
        town_city: clientData.town_city || "",
        status: clientData.status || "active",
        reference_code: clientData.reference_code || "",
        website: clientData.website || "",
        currency: clientData.currency || "",
        vat_rate: clientData.vat_rate?.toString() || "",
        vat_code: clientData.vat_code || "",
        fax: clientData.fax || "",
        notes: clientData.notes || "",
        logo_url: clientData.logo_url || "",
        portal_display_name: clientData.portal_display_name || "",
        portal_notes: clientData.portal_notes || "",
        // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1 — safe defaults
        // for unmigrated client records. Do NOT silently overwrite an
        // explicitly-set backend value.
        billing_country: clientData.billing_country || "",
        client_entity_type: clientData.client_entity_type || DEFAULT_CLIENT_ENTITY_TYPE,
        vat_number_status: clientData.vat_number_status || DEFAULT_VAT_NUMBER_STATUS,
        client_tax_treatment: clientData.client_tax_treatment || DEFAULT_CLIENT_TAX_TREATMENT,
      });
    } catch (err) {
      toast.error("Failed to load client");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  }, [clientId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const updateData = {
        name: formData.name,
        contact_email: formData.contact_email || null,
        contact_phone: formData.contact_phone || null,
        billing_address: formData.billing_address || null,
        // EVENTSFLO-LIVE-DATA-ENTRY-VENUE-LOCATION-FILENAME-ADDRESS-UX-V1
        // EVENTSFLO-CLIENT-VENUE-ADDRESS-UI-REGRESSION-FIX-V1
        address_line_1: formData.address_line_1 || null,
        address_line_2: formData.address_line_2 || null,
        postcode: formData.postcode || null,
        town_city: formData.town_city || null,
        status: formData.status,
        reference_code: formData.reference_code || null,
        website: formData.website || null,
        currency: formData.currency || null,
        vat_rate: formData.vat_rate ? parseFloat(formData.vat_rate) : null,
        vat_code: formData.vat_code || null,
        fax: formData.fax || null,
        notes: formData.notes || null,
        logo_url: formData.logo_url || null,
        portal_display_name: formData.portal_display_name || null,
        portal_notes: formData.portal_notes || null,
        // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1 — Tax Profile.
        // Trim to upper-case 2-letter ISO. Backend Pydantic enforces ≤2
        // chars; empty string → null so we never persist "".
        billing_country: (formData.billing_country || "").trim().toUpperCase().slice(0, 2) || null,
        client_entity_type: formData.client_entity_type || null,
        vat_number_status: formData.vat_number_status || null,
        client_tax_treatment: formData.client_tax_treatment || null,
      };
      
      await updateClient(clientId, updateData);
      toast.success("Client updated successfully");
      setEditing(false);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClient(clientId);
      toast.success("Client deleted successfully");
      navigate("/clients");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    // Reset form to original values
    if (client) {
      setFormData({
        name: client.name || "",
        contact_email: client.contact_email || "",
        contact_phone: client.contact_phone || "",
        billing_address: client.billing_address || "",
        status: client.status || "active",
        reference_code: client.reference_code || "",
        website: client.website || "",
        currency: client.currency || "",
        vat_rate: client.vat_rate?.toString() || "",
        vat_code: client.vat_code || "",
        fax: client.fax || "",
        notes: client.notes || "",
        logo_url: client.logo_url || "",
        portal_display_name: client.portal_display_name || "",
        portal_notes: client.portal_notes || "",
        // EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1
        billing_country: client.billing_country || "",
        client_entity_type: client.client_entity_type || DEFAULT_CLIENT_ENTITY_TYPE,
        vat_number_status: client.vat_number_status || DEFAULT_VAT_NUMBER_STATUS,
        client_tax_treatment: client.client_tax_treatment || DEFAULT_CLIENT_TAX_TREATMENT,
      });
    }
  };

  // EVENTSFLO-ADMIN-CLIENT-QUOTE-POST-TO-PORTAL-UX-V1
  // Mirrors backend `CLIENT_VISIBLE_QUOTE_STATUSES` in server.py.
  const CLIENT_PORTAL_VISIBLE_STATUSES = new Set([
    "sent", "approved", "accepted", "rejected", "expired",
  ]);
  const isVisibleInPortal = (status) =>
    CLIENT_PORTAL_VISIBLE_STATUSES.has(String(status || "").toLowerCase());

  // EVENTSFLO-CLIENT-PROFILE-VENUE-LOCATIONS-AND-REMOVE-ASSOCIATION-V1
  // Determine the association type between the current client and a
  // venue row so the UI can gate the "Remove from Client" action.
  const getVenueAssociationType = useCallback((venue) => {
    if (!venue || !clientId) return "none";
    const copiedFor = venue.copied_for_client_id === clientId;
    const isCopy = venue.is_client_copy === true && venue.client_id === clientId;
    if (copiedFor || isCopy) return "client_copy";
    const shared = Array.isArray(venue.shared_client_ids) ? venue.shared_client_ids : [];
    if (shared.includes(clientId) && venue.client_id !== clientId) return "shared";
    if (venue.client_id === clientId) return "owned";
    return "none";
  }, [clientId]);

  // Toggle venue row expansion — lazy-loads the venue's locations.
  const handleToggleVenue = useCallback(async (venueId) => {
    if (expandedVenueId === venueId) {
      setExpandedVenueId(null);
      return;
    }
    setExpandedVenueId(venueId);
    if (venueLocationsByVenue[venueId] !== undefined) return; // cached
    setVenueLocationsLoading(true);
    try {
      const res = await getVenueLocations({ venue_id: venueId, limit: 500 });
      setVenueLocationsByVenue((prev) => ({
        ...prev,
        [venueId]: res.data || [],
      }));
    } catch (err) {
      toast.error(`Could not load locations: ${extractErrorMessage(err)}`);
      setVenueLocationsByVenue((prev) => ({ ...prev, [venueId]: [] }));
    } finally {
      setVenueLocationsLoading(false);
    }
  }, [expandedVenueId, venueLocationsByVenue]);

  // Remove-from-Client confirm handler.
  const handleConfirmRemoveAssociation = async () => {
    if (!removeAssocVenue) return;
    setRemovingAssoc(true);
    try {
      const res = await removeClientVenueAssociation(clientId, removeAssocVenue.id);
      const assoc = res.data?.association_type;
      toast.success(
        assoc === "client_copy"
          ? `Client copy removed from ${client?.name || "this client"}`
          : `${removeAssocVenue.name} unshared from ${client?.name || "this client"}`
      );
      // Refresh venues list in Client profile (drop the removed row).
      setVenues((prev) => prev.filter((v) => v.id !== removeAssocVenue.id));
      // Clear expanded state if the removed row was expanded.
      if (expandedVenueId === removeAssocVenue.id) {
        setExpandedVenueId(null);
      }
      setRemoveAssocVenue(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setRemovingAssoc(false);
    }
  };

  const handlePostToPortal = async (quote) => {
    const ok = window.confirm(
      `This will make quote ${quote.quote_number} visible to ${client?.name || "this client"} in their Client Portal.\n\nNo email will be sent.\n\nProceed?`
    );
    if (!ok) return;
    try {
      await updateQuote(quote.id, { status: "sent" });
      toast.success(`${quote.quote_number} is now visible in ${client?.name || "the client"}'s Client Portal.`);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to post quote to Client Portal");
    }
  };

  // EVENTSFLO-QUOTE-LIFECYCLE-CLIENT-ACCEPTANCE-ADMIN-SEND-V1
  const handleSendByEmail = async (quote) => {
    const recipient = client?.primary_contact_email || client?.email;
    if (!recipient) {
      toast.error("No client email address is available for this quote.");
      return;
    }
    const ok = window.confirm(
      `Send ${quote.quote_number} to ${recipient}?\n\nThis will mark the quote as Sent and make it visible in the Client Portal.`
    );
    if (!ok) return;
    try {
      await sendQuoteByEmailDefault(quote.id, recipient);
      // The send-email endpoint does not auto-update status. Flip it here.
      if (String(quote.status || "").toLowerCase() === "draft") {
        await updateQuote(quote.id, { status: "sent" }).catch(() => {});
      }
      toast.success(`${quote.quote_number} was emailed to ${recipient} and is now visible in ${client?.name || "the client"}'s Client Portal.`);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to send email — check email provider in Settings.");
    }
  };

  const handleMarkAccepted = async (quote) => {
    const ok = window.confirm(
      `Mark ${quote.quote_number} as accepted?\n\nUse this only when the client has accepted outside the portal (email, phone, signed PDF).\n\nNo email will be sent. No invoice will be created.`
    );
    if (!ok) return;
    // EVENTSFLO-QUOTE-ACCEPTANCE-AUDIT-NOTES-V1: optional internal admin note
    const note = window.prompt(
      "Optional internal note (e.g. 'Accepted by email from John', 'Signed PDF received'). Visible to admins only — leave blank to skip.",
      ""
    );
    try {
      await adminMarkQuoteAccepted(quote.id, note || undefined);
      toast.success(`${quote.quote_number} marked as accepted.`);
      loadData();
    } catch (err) {
      toast.error(extractErrorMessage(err) || "Failed to mark quote accepted");
    }
  };

  // EVENTSFLO-CLIENT-PROFILE-CUSTOMISATION-LOGO-V1
  const API_URL = process.env.REACT_APP_BACKEND_URL || "";
  const authHeaders = () => {
    const token = localStorage.getItem("eventsflo_auth_token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
      toast.error("Logo must be JPEG, PNG, WEBP, or GIF");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Logo too large (max 5 MB)");
      return;
    }
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await fetch(`${API_URL}/api/clients/${clientId}/logo`, {
        method: "POST",
        headers: { ...authHeaders() },
        body: fd,
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Upload failed (${r.status})`);
      }
      toast.success("Client logo updated");
      await loadData();
    } catch (e) {
      toast.error(e.message || "Logo upload failed");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    setLogoUploading(true);
    try {
      const r = await fetch(`${API_URL}/api/clients/${clientId}/logo`, {
        method: "DELETE",
        headers: { ...authHeaders() },
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `Remove failed (${r.status})`);
      }
      toast.success("Client logo removed");
      await loadData();
    } catch (e) {
      toast.error(e.message || "Logo remove failed");
    } finally {
      setLogoUploading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  if (!client) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Client not found</p>
        <Button onClick={() => navigate("/clients")} className="mt-4">
          Back to Clients
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="client-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/clients")}
            data-testid="back-to-clients"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold font-secondary tracking-tight uppercase flex items-center gap-3">
              <Building2 className="h-8 w-8 text-primary" />
              {client.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <StatusBadge status={client.status} />
              {client.reference_code && (
                <span className="text-sm text-muted-foreground font-mono">
                  Ref: {client.reference_code}
                </span>
              )}
              <span className="text-sm text-muted-foreground">
                Created {formatDate(client.created_at)}
              </span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {editing ? (
            <>
              <Button variant="outline" onClick={handleCancel} disabled={submitting}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={submitting} data-testid="save-client-btn">
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-client-btn">
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                onClick={() => setDeleteDialogOpen(true)}
                data-testid="delete-client-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="details" className="space-y-4">
        <TabsList>
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            <Users className="h-4 w-4 mr-1" />
            Contacts
          </TabsTrigger>
          <TabsTrigger value="venues" data-testid="tab-venues">
            Venues ({venues.length})
          </TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects">
            Projects ({projects.length})
          </TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes">
            <FileText className="h-4 w-4 mr-1" />
            Quotes ({quotes.length})
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <Receipt className="h-4 w-4 mr-1" />
            Invoices ({invoices.length})
          </TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Contact Information */}
            <Card className="bg-card border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-xs text-muted-foreground">Company Name</Label>
                  {editing ? (
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="client-name-input"
                    />
                  ) : (
                    <p className="text-sm font-medium">{client.name}</p>
                  )}
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Email</Label>
                  {editing ? (
                    <Input
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      data-testid="client-email-input"
                    />
                  ) : (
                    <p className="text-sm">
                      {client.contact_email ? (
                        <a href={`mailto:${client.contact_email}`} className="text-primary hover:underline">
                          {client.contact_email}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </p>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Phone</Label>
                    {editing ? (
                      <Input
                        value={formData.contact_phone}
                        onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                        data-testid="client-phone-input"
                      />
                    ) : (
                      <p className="text-sm">
                        {client.contact_phone ? (
                          <a href={`tel:${client.contact_phone}`} className="hover:text-primary">
                            {client.contact_phone}
                          </a>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Fax</Label>
                    {editing ? (
                      <Input
                        value={formData.fax}
                        onChange={(e) => setFormData({ ...formData, fax: e.target.value })}
                        data-testid="client-fax-input"
                      />
                    ) : (
                      <p className="text-sm">{client.fax || <span className="text-muted-foreground">—</span>}</p>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Website</Label>
                  {editing ? (
                    <Input
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      placeholder="https://example.com"
                      data-testid="client-website-input"
                    />
                  ) : (
                    <p className="text-sm">
                      {client.website ? (
                        <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-primary hover:underline flex items-center gap-1">
                          {client.website}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Billing & Address */}
            <Card className="bg-card border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Billing & Address
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* EVENTSFLO-CLIENT-VENUE-ADDRESS-UI-REGRESSION-FIX-V1:
                    Structured address (address_line_1, address_line_2,
                    postcode, town_city, billing_country) rendered first
                    with readable Labels. Legacy single-line billing_address
                    moved below into a collapsible details block. */}
                <div className="space-y-3">
                  <div>
                    <Label htmlFor="edit-address-line-1">Address Line 1</Label>
                    {editing ? (
                      <Input
                        id="edit-address-line-1"
                        value={formData.address_line_1}
                        onChange={(e) => setFormData({ ...formData, address_line_1: e.target.value })}
                        placeholder="Street / building"
                        data-testid="client-address-line-1-input"
                      />
                    ) : (
                      <p className="text-sm">{client.address_line_1 || <span className="text-muted-foreground">—</span>}</p>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="edit-address-line-2">Address Line 2</Label>
                    {editing ? (
                      <Input
                        id="edit-address-line-2"
                        value={formData.address_line_2}
                        onChange={(e) => setFormData({ ...formData, address_line_2: e.target.value })}
                        placeholder="Optional"
                        data-testid="client-address-line-2-input"
                      />
                    ) : (
                      <p className="text-sm">{client.address_line_2 || <span className="text-muted-foreground">—</span>}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="edit-postcode">Postcode</Label>
                      {editing ? (
                        <Input
                          id="edit-postcode"
                          value={formData.postcode}
                          onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                          data-testid="client-postcode-input"
                        />
                      ) : (
                        <p className="text-sm">{client.postcode || <span className="text-muted-foreground">—</span>}</p>
                      )}
                    </div>
                    <div>
                      <Label htmlFor="edit-town-city">City</Label>
                      {editing ? (
                        <Input
                          id="edit-town-city"
                          value={formData.town_city}
                          onChange={(e) => setFormData({ ...formData, town_city: e.target.value })}
                          data-testid="client-town-city-input"
                        />
                      ) : (
                        <p className="text-sm">{client.town_city || <span className="text-muted-foreground">—</span>}</p>
                      )}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="edit-billing-country">Country</Label>
                    {editing ? (
                      // EVENTSFLO-UNIVERSAL-COUNTRY-DROPDOWN-AND-VENUE-ADDRESS-FIELDS-V1
                      <CountrySelect
                        value={formData.billing_country}
                        onValueChange={(v) =>
                          setFormData({ ...formData, billing_country: (v || "").toUpperCase().slice(0, 2) })
                        }
                        valueMode="iso2"
                        placeholder="Search country..."
                        testId="client-country-input"
                      />
                    ) : (
                      <p className="text-sm">{client.billing_country || <span className="text-muted-foreground">—</span>}</p>
                    )}
                  </div>
                </div>
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer select-none py-1 hover:text-foreground">
                    Advanced / legacy single-line address
                  </summary>
                  <div className="pt-2">
                    {editing ? (
                      <Textarea
                        value={formData.billing_address}
                        onChange={(e) => setFormData({ ...formData, billing_address: e.target.value })}
                        rows={3}
                        placeholder="Legacy single-line address (backward compatibility)"
                        data-testid="client-address-input"
                      />
                    ) : (
                      <p className="text-sm whitespace-pre-line">
                        {client.billing_address || <span className="text-muted-foreground">No legacy address</span>}
                      </p>
                    )}
                  </div>
                </details>
                
                <div>
                  <Label className="text-xs text-muted-foreground">Status</Label>
                  {editing ? (
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger data-testid="client-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-1">
                      <StatusBadge status={client.status} />
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Financial Details */}
            <Card className="bg-card border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Financial Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Currency</Label>
                    {editing ? (
                      <Input
                        value={formData.currency}
                        onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                        placeholder="EUR, GBP, USD..."
                        data-testid="client-currency-input"
                      />
                    ) : (
                      <p className="text-sm font-mono">{client.currency || <span className="text-muted-foreground">Default</span>}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">VAT / Tax Number</Label>
                    {editing ? (
                      <Input
                        value={formData.vat_code}
                        onChange={(e) => setFormData({ ...formData, vat_code: e.target.value })}
                        placeholder="GB 123456789"
                        data-testid="client-vat-number-input"
                      />
                    ) : (
                      <p className="text-sm font-mono">{client.vat_code || <span className="text-muted-foreground">—</span>}</p>
                    )}
                  </div>
                </div>

                {/* EVENTSFLO-CLIENT-TAX-CODE-VALIDATION-MULTI-COUNTRY-V1 */}
                <ClientTaxCodeValidation
                  clientId={client.id}
                  vatCode={editing ? formData.vat_code : client.vat_code}
                  billingCountry={editing ? formData.billing_country : client.billing_country}
                  initialSnapshot={client.tax_validation || null}
                  onApplied={(updated) => setClient(updated)}
                />
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">VAT Rate (%)</Label>
                    {editing ? (
                      <Input
                        type="number"
                        step="0.1"
                        value={formData.vat_rate}
                        onChange={(e) => setFormData({ ...formData, vat_rate: e.target.value })}
                        placeholder="20"
                        data-testid="client-vatrate-input"
                      />
                    ) : (
                      <p className="text-sm">
                        {client.vat_rate != null ? (
                          <span className="flex items-center gap-1">
                            {client.vat_rate}
                            <Percent className="h-3 w-3" />
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </p>
                    )}
                  </div>
                  
                  <div>
                    <Label className="text-xs text-muted-foreground">Reference Code</Label>
                    {editing ? (
                      <Input
                        value={formData.reference_code}
                        onChange={(e) => setFormData({ ...formData, reference_code: e.target.value })}
                        placeholder="Client reference"
                        data-testid="client-refcode-input"
                      />
                    ) : (
                      <p className="text-sm font-mono">{client.reference_code || <span className="text-muted-foreground">—</span>}</p>
                    )}
                  </div>
                </div>

                {/* EVENTSFLO-FINANCIAL-TAX-RULES-MATRIX-ADMIN-UI-V1 — Tax Profile */}
                <div className="border-t border-white/10 pt-4 mt-2">
                  <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-2">
                    Tax Profile
                    {/* EVENTSFLO-FINANCIAL-TAX-RULES-LINE-LEVEL-AUDIT-VIEW-V1 */}
                    <HelpPopover
                      testId="client-tax-profile-help"
                      title="How client tax treatment works"
                    >
                      <p>
                        Client tax treatment works together with each task&apos;s
                        <strong> Tax Class</strong> and the Admin
                        <strong> Tax Rules Matrix </strong>
                        to decide the VAT / TVA on quote and invoice lines.
                      </p>
                      <p>
                        Monaco / France domestic clients normally charge
                        <strong> TVA 20%</strong>.
                      </p>
                      <p>
                        <strong>EU B2B reverse charge</strong> should only be
                        used when the client&apos;s VAT number is valid and the
                        service is eligible.
                      </p>
                      <p>
                        For a <strong>UK VAT-registered business</strong> such
                        as Opus, use billing country <code>GB</code>, entity
                        {' '}<code>Business</code>, VAT status
                        {' '}<code>Valid</code>, and the configured
                        {' '}<code>non_eu_b2b_services</code> treatment.
                      </p>
                      <p className="text-amber-500">
                        If unsure, confirm treatment with your accountant.
                      </p>
                    </HelpPopover>
                  </h4>
                  <p className="text-xs text-muted-foreground mb-3">
                    Client tax treatment is used with each task&apos;s tax class and the Admin Tax Rules Matrix to determine VAT/TVA treatment on quote and invoice lines.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">Billing Country</Label>
                      {editing ? (
                        <Input
                          value={formData.billing_country}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              billing_country: e.target.value.toUpperCase().slice(0, 2),
                            })
                          }
                          placeholder="FR / MC / GB"
                          maxLength={2}
                          data-testid="client-billing-country-input"
                        />
                      ) : (
                        <p className="text-sm font-mono">{client.billing_country || <span className="text-muted-foreground">—</span>}</p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Entity Type</Label>
                      {editing ? (
                        <Select
                          value={formData.client_entity_type || DEFAULT_CLIENT_ENTITY_TYPE}
                          onValueChange={(value) => setFormData({ ...formData, client_entity_type: value })}
                        >
                          <SelectTrigger data-testid="client-entity-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CLIENT_ENTITY_TYPES.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm">
                          {CLIENT_ENTITY_TYPES.find((o) => o.value === client.client_entity_type)?.label
                            || <span className="text-muted-foreground">—</span>}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <Label className="text-xs text-muted-foreground">VAT Number Status</Label>
                      {editing ? (
                        <Select
                          value={formData.vat_number_status || DEFAULT_VAT_NUMBER_STATUS}
                          onValueChange={(value) => setFormData({ ...formData, vat_number_status: value })}
                        >
                          <SelectTrigger data-testid="client-vat-number-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {VAT_NUMBER_STATUSES.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm">
                          {VAT_NUMBER_STATUSES.find((o) => o.value === client.vat_number_status)?.label
                            || <span className="text-muted-foreground">—</span>}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label className="text-xs text-muted-foreground">Client Tax Treatment</Label>
                      {editing ? (
                        <Select
                          value={formData.client_tax_treatment || DEFAULT_CLIENT_TAX_TREATMENT}
                          onValueChange={(value) => setFormData({ ...formData, client_tax_treatment: value })}
                        >
                          <SelectTrigger data-testid="client-tax-treatment-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CLIENT_TAX_TREATMENTS.map((o) => (
                              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm">
                          {CLIENT_TAX_TREATMENTS.find((o) => o.value === client.client_tax_treatment)?.label
                            || <span className="text-muted-foreground">—</span>}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="bg-card border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Notes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {editing ? (
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={4}
                    placeholder="Add notes about this client..."
                    data-testid="client-notes-input"
                  />
                ) : (
                  <p className="text-sm whitespace-pre-line">
                    {client.notes || <span className="text-muted-foreground">No notes</span>}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* EVENTSFLO-CLIENT-PROFILE-CUSTOMISATION-LOGO-V1 */}
            <Card
              className="bg-card border-white/5 lg:col-span-2"
              data-testid="client-portal-customisation-card"
            >
              <CardHeader className="pb-3">
                <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Client Portal Customisation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-muted-foreground">
                  This logo is shown in the Client Portal for this client.
                  Tenant emails still use the tenant brand unless email
                  templates explicitly include client branding.
                </p>
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex flex-col items-start gap-2">
                    <Label className="text-xs text-muted-foreground">
                      Logo
                    </Label>
                    {client.logo_url ? (
                      <img
                        src={client.logo_url}
                        alt={`${client.name} logo`}
                        className="h-20 w-20 object-contain rounded border border-border bg-background p-1"
                        data-testid="client-logo-preview"
                      />
                    ) : (
                      <div
                        className="h-20 w-20 flex items-center justify-center rounded border border-dashed border-border text-[10px] text-muted-foreground text-center px-2"
                        data-testid="client-logo-empty"
                      >
                        No logo
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label
                        className="cursor-pointer inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-border hover:bg-surface-elevated/40"
                        data-testid="client-logo-upload-label"
                      >
                        {logoUploading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Pencil className="h-3 w-3" />
                        )}
                        {client.logo_url ? "Replace" : "Upload"}
                        <input
                          type="file"
                          className="hidden"
                          accept="image/jpeg,image/png,image/webp,image/gif"
                          disabled={logoUploading}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) handleLogoUpload(f);
                            e.target.value = "";
                          }}
                          data-testid="client-logo-upload-input"
                        />
                      </label>
                      {client.logo_url && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          disabled={logoUploading}
                          onClick={handleLogoRemove}
                          data-testid="client-logo-remove-btn"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      JPEG, PNG, WEBP, GIF · max 5&nbsp;MB · auto-resized to 600px
                    </p>
                  </div>
                  <div className="flex-1 min-w-[220px] space-y-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Portal display name
                      </Label>
                      {editing ? (
                        <Input
                          value={formData.portal_display_name}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              portal_display_name: e.target.value,
                            })
                          }
                          placeholder={client.name}
                          maxLength={200}
                          data-testid="client-portal-display-name-input"
                        />
                      ) : (
                        <p className="text-sm" data-testid="client-portal-display-name-value">
                          {client.portal_display_name || (
                            <span className="text-muted-foreground">
                              (defaults to "{client.name}")
                            </span>
                          )}
                        </p>
                      )}
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Optional. Shown in the Client Portal header instead
                        of the company name.
                      </p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Portal notes (admin only)
                      </Label>
                      {editing ? (
                        <Textarea
                          value={formData.portal_notes}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              portal_notes: e.target.value,
                            })
                          }
                          rows={3}
                          maxLength={2000}
                          placeholder="Internal customisation notes (not shown to the client)…"
                          data-testid="client-portal-notes-input"
                        />
                      ) : (
                        <p className="text-sm whitespace-pre-line" data-testid="client-portal-notes-value">
                          {client.portal_notes || (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Contacts Tab - MVP Critical for Quotes, Estimates, Invoices */}
        <TabsContent value="contacts">
          <ClientContactsSection clientId={clientId} readOnly={false} />
        </TabsContent>

        {/* Venues Tab */}
        <TabsContent value="venues">
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                Venues ({venues.length})
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/venues")}
              >
                Manage Venues
              </Button>
            </CardHeader>
            <CardContent>
              {venues.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  No venues for this client
                </p>
              ) : (
                <div className="space-y-2">
                  {venues.map((venue) => {
                    // EVENTSFLO-CLIENT-SCOPED-VENUE-LOCATION-FILTERING-V1
                    // EVENTSFLO-CLIENT-PROFILE-VENUE-LOCATIONS-AND-REMOVE-ASSOCIATION-V1
                    // Badge classification MUST match the association
                    // helper: "Client copy" only when this specific
                    // client is the copy target (`copied_for_client_id`).
                    // A venue that is a copy for a *different* client
                    // but shared to us is still "Shared" from our view.
                    const isCopy = venue.copied_for_client_id === clientId;
                    const isOwned = !isCopy && venue.client_id === clientId;
                    const isShared = (
                      !isCopy && !isOwned &&
                      Array.isArray(venue.shared_client_ids) &&
                      venue.shared_client_ids.includes(clientId)
                    );
                    const badgeLabel = isCopy ? "Client copy" : isShared ? "Shared" : isOwned ? "Owned" : "";
                    const badgeClass = isCopy
                      ? "bg-purple-500/20 text-purple-300 border-purple-500/30"
                      : isShared
                        ? "bg-blue-500/20 text-blue-300 border-blue-500/30"
                        : "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
                    // EVENTSFLO-CLIENT-PROFILE-VENUE-LOCATIONS-AND-REMOVE-ASSOCIATION-V1
                    const assocType = getVenueAssociationType(venue);
                    const canRemove = assocType === "shared" || assocType === "client_copy";
                    const isExpanded = expandedVenueId === venue.id;
                    const locations = venueLocationsByVenue[venue.id];
                    return (
                      <div
                        key={venue.id}
                        className="bg-surface-elevated/20 rounded overflow-hidden"
                        data-testid={`venue-item-${venue.id}`}
                      >
                        <div className="flex items-center justify-between p-3 hover:bg-surface-elevated/30 transition-colors">
                          <button
                            type="button"
                            className="flex-1 flex items-start gap-2 text-left cursor-pointer"
                            onClick={() => handleToggleVenue(venue.id)}
                            data-testid={`venue-expand-${venue.id}`}
                          >
                            <ChevronRight
                              className={`h-4 w-4 mt-0.5 text-muted-foreground transition-transform ${isExpanded ? "rotate-90" : ""}`}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-sm">{venue.name}</p>
                                {badgeLabel && (
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded border ${badgeClass}`}
                                    data-testid={`venue-visibility-badge-${venue.id}`}
                                  >
                                    {badgeLabel}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground">
                                {venue.address}{venue.city && `, ${venue.city}`}{venue.postcode && ` ${venue.postcode}`}
                              </p>
                            </div>
                          </button>
                          <div className="flex items-center gap-1 ml-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-muted-foreground hover:text-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/venues?edit=${venue.id}`);
                              }}
                              title="Edit Venue in Venue Library"
                              data-testid={`venue-edit-${venue.id}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            {canRemove && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRemoveAssocVenue(venue);
                                }}
                                title="Remove from Client"
                                data-testid={`venue-remove-${venue.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                        {isExpanded && (
                          <div
                            className="border-t border-white/5 bg-black/20 px-6 py-3"
                            data-testid={`venue-locations-${venue.id}`}
                          >
                            {venueLocationsLoading && locations === undefined ? (
                              <p className="text-xs text-muted-foreground py-2">Loading locations…</p>
                            ) : locations && locations.length > 0 ? (
                              <ul className="space-y-1.5">
                                {locations.map((loc) => (
                                  <li
                                    key={loc.id}
                                    className="flex items-start gap-2 text-xs"
                                    data-testid={`venue-location-item-${loc.id}`}
                                  >
                                    <MapPin className="h-3 w-3 mt-0.5 text-muted-foreground" />
                                    <div>
                                      <p className="font-medium text-foreground">{loc.name}</p>
                                      <p className="text-muted-foreground">
                                        {[loc.address1, loc.city, loc.postcode, loc.country].filter(Boolean).join(", ")}
                                      </p>
                                    </div>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p
                                className="text-xs text-muted-foreground py-2"
                                data-testid={`venue-locations-empty-${venue.id}`}
                              >
                                No locations registered for this Venue.
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Projects Tab */}
        <TabsContent value="projects">
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                Projects ({projects.length})
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/projects")}
              >
                Manage Projects
              </Button>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  No projects for this client
                </p>
              ) : (
                <div className="space-y-2">
                  {projects.map((project) => (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      className="flex items-center justify-between p-3 bg-surface-elevated/20 rounded hover:bg-surface-elevated/30 transition-colors"
                      data-testid={`project-item-${project.id}`}
                    >
                      <div>
                        <p className="font-medium text-sm flex items-center gap-2">
                          <FolderKanban className="h-4 w-4 text-primary" />
                          {project.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(project.start_date)} - {formatDate(project.end_date)}
                        </p>
                      </div>
                      <StatusBadge status={project.status} />
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quotes Tab — EVENTSFLO-ADMIN-CLIENT-QUOTES-INVOICES-VISIBILITY-DIAGNOSTIC-FIX-V1 */}
        {/* EVENTSFLO-ADMIN-CLIENT-QUOTE-POST-TO-PORTAL-UX-V1: portal-visibility badges + Post action */}
        <TabsContent value="quotes">
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                Quotes ({quotes.length})
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/quotes")}
                data-testid="manage-quotes-btn"
              >
                Manage Quotes
              </Button>
            </CardHeader>
            <CardContent>
              <p
                className="text-xs text-muted-foreground mb-3"
                data-testid="quotes-portal-helper-text"
              >
                Draft quotes are admin-only. Use <span className="font-medium">Post to Client Portal</span> to make a quote visible to this client. No email is sent.
              </p>
              {quotes.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  No quotes for this client yet
                </p>
              ) : (
                <div className="space-y-2">
                  {quotes.map((quote) => {
                    const project = projects.find(p => p.id === quote.project_id);
                    const visible = isVisibleInPortal(quote.status);
                    return (
                      <div
                        key={quote.id}
                        className="flex items-center justify-between p-3 bg-surface-elevated/20 rounded hover:bg-surface-elevated/30 transition-colors"
                        data-testid={`client-quote-${quote.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm flex items-center gap-2 flex-wrap">
                            <FileText className="h-4 w-4 text-primary" />
                            {quote.quote_number || "(no number)"}
                            <StatusBadge status={quote.status} />
                            {visible ? (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                                data-testid={`portal-visibility-visible-${quote.id}`}
                                title="Visible in Client Portal"
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                Visible in portal
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30"
                                data-testid={`portal-visibility-hidden-${quote.id}`}
                                title="Not visible in Client Portal until you post it"
                              >
                                <EyeOff className="h-3 w-3" />
                                Not visible in portal
                              </span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground truncate mt-1">
                            {project?.name || "—"} · Issued {formatDate(quote.issue_date)}
                            {quote.valid_until ? ` · Valid until ${formatDate(quote.valid_until)}` : ""}
                          </p>
                          {/* EVENTSFLO-QUOTE-ACCEPTANCE-AUDIT-NOTES-V1 */}
                          {String(quote.status || "").toLowerCase() === "accepted" && quote.accepted_at && (
                            <p
                              className="text-xs text-emerald-300 mt-1 flex items-center gap-1"
                              data-testid={`client-quote-acceptance-meta-${quote.id}`}
                            >
                              <CheckCircle2 className="h-3 w-3" />
                              Accepted {new Date(quote.accepted_at).toLocaleString()} ·{" "}
                              {quote.acceptance_source === "client_portal"
                                ? "via Client Portal"
                                : "marked by Admin"}
                              {quote.accepted_by_name ? ` (${quote.accepted_by_name})` : ""}
                            </p>
                          )}
                          {quote.activity_log && quote.activity_log.length > 0 && (
                            <details className="mt-1 text-xs" data-testid={`client-quote-activity-${quote.id}`}>
                              <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                                Activity ({quote.activity_log.length})
                              </summary>
                              <ul className="mt-1 pl-3 space-y-0.5">
                                {quote.activity_log.map((ev, i) => (
                                  <li key={ev.id || i} className="text-muted-foreground">
                                    <span className="text-foreground">
                                      {ev.created_at ? new Date(ev.created_at).toLocaleString() : ""}
                                    </span>
                                    {" — "}
                                    {ev.message}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          )}
                        </div>
                        <div className="flex items-center gap-3 ml-3">
                          <span className="text-sm font-medium tabular-nums">
                            {formatCurrency(quote.total ?? 0)}
                          </span>
                          {String(quote.status || "").toLowerCase() === "draft" && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handlePostToPortal(quote)}
                                data-testid={`client-quote-post-portal-${quote.id}`}
                                title="Make this quote visible in the Client Portal (no email sent)"
                              >
                                <Send className="h-4 w-4 mr-1" />
                                Post to Client Portal
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleSendByEmail(quote)}
                                data-testid={`client-quote-send-email-${quote.id}`}
                                title="Email this quote to the client's primary contact"
                              >
                                <Mail className="h-4 w-4 mr-1" />
                                Send by Email
                              </Button>
                            </>
                          )}
                          {["sent", "approved"].includes(String(quote.status || "").toLowerCase()) && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMarkAccepted(quote)}
                              data-testid={`client-quote-mark-accepted-${quote.id}`}
                              title="Mark accepted (client accepted offline — no email sent)"
                            >
                              <ThumbsUp className="h-4 w-4 mr-1" />
                              Mark Accepted
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewQuotePdf(quote.id, quote.quote_number).catch(() => toast.error("Failed to open PDF"))}
                            data-testid={`client-quote-view-${quote.id}`}
                            title="View PDF"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadQuotePdf(quote.id, quote.quote_number).catch(() => toast.error("Failed to download PDF"))}
                            data-testid={`client-quote-download-${quote.id}`}
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/quotes`)}
                            data-testid={`client-quote-open-${quote.id}`}
                            title="Manage in Quotes list"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Invoices Tab — EVENTSFLO-ADMIN-CLIENT-QUOTES-INVOICES-VISIBILITY-DIAGNOSTIC-FIX-V1 */}
        <TabsContent value="invoices">
          <Card className="bg-card border-white/5">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-wider text-muted-foreground">
                Invoices ({invoices.length})
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/invoices")}
                data-testid="manage-invoices-btn"
              >
                Manage Invoices
              </Button>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  No invoices for this client yet
                </p>
              ) : (
                <div className="space-y-2">
                  {invoices.map((invoice) => {
                    const project = projects.find(p => p.id === invoice.project_id);
                    const outstanding = (invoice.total ?? 0) - (invoice.amount_paid ?? 0);
                    return (
                      <div
                        key={invoice.id}
                        className="flex items-center justify-between p-3 bg-surface-elevated/20 rounded hover:bg-surface-elevated/30 transition-colors"
                        data-testid={`client-invoice-${invoice.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm flex items-center gap-2">
                            <Receipt className="h-4 w-4 text-primary" />
                            {invoice.invoice_number || "(draft – no number yet)"}
                            <StatusBadge status={invoice.status} />
                          </p>
                          <p className="text-xs text-muted-foreground truncate">
                            {project?.name || "—"} · Issued {formatDate(invoice.issue_date)}
                            {invoice.due_date ? ` · Due ${formatDate(invoice.due_date)}` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-3 ml-3">
                          <div className="text-right">
                            <p className="text-sm font-medium tabular-nums">
                              {formatCurrency(invoice.total ?? 0)}
                            </p>
                            {outstanding > 0 && (invoice.amount_paid ?? 0) > 0 && (
                              <p className="text-xs text-muted-foreground tabular-nums">
                                {formatCurrency(outstanding)} outstanding
                              </p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewInvoicePdf(invoice.id, invoice.invoice_number).catch(() => toast.error("Failed to open PDF"))}
                            data-testid={`client-invoice-view-${invoice.id}`}
                            title="View PDF"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => downloadInvoicePdf(invoice.id, invoice.invoice_number).catch(() => toast.error("Failed to download PDF"))}
                            data-testid={`client-invoice-download-${invoice.id}`}
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/invoices`)}
                            data-testid={`client-invoice-open-${invoice.id}`}
                            title="Manage in Invoices list"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Delete Confirmation */}
      <DestructiveConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete Client"
        itemName={client.name}
        itemType="client"
        description={`Are you sure you want to delete "${client.name}"? This may affect ${venues.length} venues and ${projects.length} projects. This action is permanent and cannot be undone.`}
        onConfirm={handleDelete}
        loading={deleting}
      />

      {/* EVENTSFLO-CLIENT-PROFILE-VENUE-LOCATIONS-AND-REMOVE-ASSOCIATION-V1
          Client-Venue association removal — non-destructive: never
          deletes the venue or its locations. */}
      {removeAssocVenue && (
        <DestructiveConfirmDialog
          open={!!removeAssocVenue}
          onOpenChange={(open) => { if (!open) setRemoveAssocVenue(null); }}
          title="Remove Venue from Client"
          itemName={removeAssocVenue.name}
          itemType="venue association"
          description={
            getVenueAssociationType(removeAssocVenue) === "client_copy"
              ? `Remove this Client copy from ${client?.name || "this client"}? This will remove it from this Client's Venue list and future Project/Shift selectors. The venue record is preserved (soft-archived) — no locations are deleted.`
              : `Remove this shared Venue from ${client?.name || "this client"}? The Venue and its Locations will not be deleted, but it will no longer appear for this Client.`
          }
          confirmText="Remove from Client"
          onConfirm={handleConfirmRemoveAssociation}
          loading={removingAssoc}
          data-testid="remove-venue-association-dialog"
        />
      )}
    </div>
  );
}
